import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { AppInsightsService, PagePayload, ProviderService, ToastMessageService } from '@k12/app-services';
import { EmployeeSummary, ModalType, ProviderType, SeverityLevel, SubService, SvgIcon, ToastMessageType } from '@k12/models';
import { TruncatePipe } from '@k12/pipes';
import { CfiMatTableComponent, ColumnDefinition } from '@k12/tables';
import { ModalDialogComponent } from '@k12/ui-primitives';
import { TASKS_URL, WORK_QUEUE_URL } from '@k12/user-records';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { take, takeUntil, Subject } from 'rxjs';

const MEDIUM_COLUMN_WIDTH = 15;
const SUB_SERVICES_COLUMN_WIDTH = 25;
const SMALL_COLUMN_WIDTH = 10;

const TRY_MESSAGE = 'Please try again later.';
const EMPLOYEE_MESSAGE = 'Failed to load employee.';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-shared-admin-employee',
  imports: [
    CfiMatTableComponent,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    NgxMatSelectSearchModule,
    ReactiveFormsModule,
    TruncatePipe
  ],
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.scss']
})
export class SharedAdminEmployeeComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input() employeeTableName: string = 'default';
  @Input() providerId: string | undefined = undefined;
  @Input() viewOnly: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  public get numEmployees(): number {
    return this.employeeFilteredData().length;
  }

  public columnDefinitions: ColumnDefinition[] = [
    {
      name: 'type',
      header: 'Provider Type',
      sortable: true,
      width: MEDIUM_COLUMN_WIDTH
    },
    {
      name: 'name',
      header: 'Employee Name',
      sortable: true,
      width: MEDIUM_COLUMN_WIDTH
    },
    {
      name: 'isOwner',
      header: 'Owner',
      sortable: true,
      width: MEDIUM_COLUMN_WIDTH
    },
    {
      name: 'subServices',
      header: 'Sub-service',
      sortable: true,
      width: SUB_SERVICES_COLUMN_WIDTH
    },
    {
      name: 'validationStatus',
      header: 'Status',
      sortable: true,
      width: MEDIUM_COLUMN_WIDTH
    },
    {
      name: 'action',
      header: 'Action',
      sortable: false,
      action: true,
      width: SMALL_COLUMN_WIDTH
    }
  ];
  public dataSource: MatTableDataSource<EmployeeSummary>;
  public displayedColumns: string[] = ['type', 'name', 'isOwner', 'subServices', 'validationStatus', 'action'];
  public employeeData: EmployeeSummary[] = [];
  employeeFilteredData = signal<EmployeeSummary[]>([]);
  employeeFilterForm: FormGroup;
  employeesFilteredList = signal<string[]>([]);
  employeesList: string[] = [];
  employeeNameSearchCtrl = new FormControl('');
  public loading = signal(true);
  public pageSize: number = 10;
  providerTypes = [
    { id: 1, name: 'Tutor', value: 'Tutor' },
    { id: 2, name: 'Therapy', value: 'Therapy' },
    { id: 3, name: 'Transportation', value: 'Transportation' }
  ];
  selectedPageIndex: number = 0;
  selectedPageRows: number = 10;
  subServicesList = signal<SubService[]>([]);
  statusList = [
    { id: 1, name: 'Yes', value: 'Yes' },
    { id: 2, name: 'More Info Needed', value: 'More Info Needed' },
    { id: 3, name: 'In Review', value: 'In Review' },
    { id: 4, name: 'No', value: 'No' }
  ];

  public readonly deactivateEmployeeIcon = SvgIcon.TrashCan;
  public readonly editEmployeeIcon = SvgIcon.Edit;

  protected onDestroy = new Subject<void>();

  private dialogRef: MatDialogRef<ModalDialogComponent>;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogService = inject(MatDialog);
  private readonly employeeFormBuilder = inject(FormBuilder);
  private readonly providerService = inject(ProviderService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastMessageService);

  constructor() {
    const pages = localStorage.getItem(`${this.employeeTableName}_employeetable_size`) ?? '10';
    this.pageSize = Number.parseInt(pages);
  }

  public ngAfterViewInit(): void {
    this.getData();
  }

  public ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  public ngOnInit(): void {
    this.getSubservices();
    this.employeeFilterForm = this.employeeFormBuilder.group({
      employeeName: [''],
      subService: [''],
      validationStatus: ['']
    });

    this.employeeNameSearchCtrl.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
      const searchName = (value || '').toLowerCase();
      this.employeesFilteredList.set(this.employeesList.filter((name) => name.toLowerCase().includes(searchName)));
    });
  }

  public onAddEmployeeClick(): void {
    this.router.navigate(['/employee/new']);
  }

  public getProviderTypeDisplay(type: string): string {
    if (type?.toLowerCase() === ProviderType.Therapy) {
      return 'Therapist';
    }
    if (type?.toLowerCase() === ProviderType.Transportation) {
      return 'Transportation';
    }

    return type ?? '';
  }

  public isOwnerEmployee(employee: EmployeeSummary): boolean {
    return employee.isOwner === true || (employee.isOwner as unknown) === 'true';
  }

  public onDeleteClick(employee: EmployeeSummary): void {
    if (this.isOwnerEmployee(employee)) {
      return;
    }
    const employeeName = employee.firstName + ' ' + employee.lastName;
    this.dialogRef = this.dialogService.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        align: 'center',
        checkMark: false,
        closeButtonText: 'No',
        confirmButtonText: 'Yes',
        message: `Are you sure that you want to delete ${employeeName}?`,
        modalType: ModalType.ConfirmModal,
        title: 'Delete Employee'
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });

    this.dialogRef.componentInstance.onConfirmModal.pipe(take(1), takeUntil(this.onDestroy)).subscribe(() => {
      this.dialogRef.close();
      this.deleteEmployee(employee.id);
    });
  }

  public onEditClick(employee: EmployeeSummary): void {
    const employeeName = employee.firstName + ' ' + employee.lastName;
    const baseRoute = this.router.url.startsWith(WORK_QUEUE_URL) ? WORK_QUEUE_URL : TASKS_URL;
    if (this.viewOnly) {
      this.router.navigate([baseRoute, this.providerId, 'employees', employee.id], {
        state: { employeeId: employee.id, employeeName: employeeName }
      });
    } else {
      this.router.navigate(['/employee', employee.id], {
        state: { employeeId: employee.id, employeeName: employeeName }
      });
    }
  }

  public updatePageSize($event: PageEvent): void {
    const x = $event?.pageSize;
    if (x) {
      localStorage.setItem(`${this.employeeTableName}_employeetable_size`, x.toString());
    }
  }

  onApplyFilters(): void {
    const employeeFormValue = this.employeeFilterForm.value;
    const selectedEmployeeName = (employeeFormValue.employeeName || '').toLowerCase();
    const selectedSubService = (employeeFormValue.subService?.name || '').toLowerCase();
    const selectedStatus = (employeeFormValue.validationStatus?.name || '').toLowerCase();

    if (!selectedEmployeeName && !selectedSubService && !selectedStatus) {
      this.employeeFilteredData.set([...this.employeeData]);
      return;
    }
    this.employeeFilteredData.set(
      this.employeeData.filter((item) => {
        const matchesEmployeeName = !selectedEmployeeName || item.name?.toLowerCase().includes(selectedEmployeeName);
        const matchesSubService =
          !selectedSubService || item.subServices?.some((sub) => sub?.name.toLowerCase().includes(selectedSubService));
        const matchesStatus = !selectedStatus || item.validationStatus?.toLowerCase().includes(selectedStatus);

        return matchesEmployeeName && matchesSubService && matchesStatus;
      })
    );
  }

  onClearFilters(): void {
    this.employeeFilterForm.reset();
    this.employeeFilterForm.setValue({
      employeeName: '',
      subService: '',
      validationStatus: ''
    });
    this.employeeNameSearchCtrl.setValue('');
    this.employeesFilteredList.set([...this.employeesList]);
    this.employeeFilteredData.set([...this.employeeData]);
  }

  private deleteEmployee(employeeId: string): void {
    this.providerService.deleteEmployee(employeeId).subscribe({
      next: () => {
        this.getData();
        this.toastService.showToast(ToastMessageType.Success, 'Employee deleted successfully.');
      },
      error: (error: { error?: { message?: string } }) => {
        const errorMessage = 'Failed to delete employee.';
        this.handleError(error, errorMessage, `${errorMessage} ${TRY_MESSAGE}`);
      }
    });
  }

  private getData(): void {
    if (this.providerId) {
      const payload = {
        filter: {
          pageNumber: this.selectedPageIndex + 1,
          pageSize: this.selectedPageRows
        }
      };
      this.getEmployeeWithId(this.providerId, payload);
    } else {
      this.getEmployee();
    }
  }

  private getEmployee(): void {
    this.providerService
      .getAllEmployeesSummary()
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe({
        next: (res: EmployeeSummary[]) => {
          this.setEmployeeTableData(res);
        },
        error: (error: { error?: { message?: string } }) => {
          this.handleError(error, EMPLOYEE_MESSAGE, `${EMPLOYEE_MESSAGE} ${TRY_MESSAGE}`);
        }
      });
  }

  private getEmployeeWithId(employeeId: string, payload: PagePayload): void {
    this.providerService.getAllEmployeesSummaryAdmin(employeeId, payload).subscribe({
      next: (res: EmployeeSummary[]) => {
        this.setEmployeeTableData(res);
      },
      error: (error: { error?: { message?: string } }) => {
        this.handleError(error, EMPLOYEE_MESSAGE, `${EMPLOYEE_MESSAGE} ${TRY_MESSAGE}`);
      }
    });
  }

  private getSubservices(employeeType?: number): void {
    this.providerService.getSubServices(employeeType).subscribe({
      next: (res: SubService[]) => {
        this.subServicesList.set(res);
      },
      error: (error: { error?: { message?: string } }) => {
        const errorMessage = 'Failed to load sub services.';
        this.handleError(error, errorMessage, `${errorMessage} ${TRY_MESSAGE}`);
      }
    });
  }

  private handleError(error: { error?: { message?: string } }, logMessage: string, toastMessage: string): void {
    const errorMessage = error?.error?.message || logMessage;
    this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
    this.toastService.showToast(ToastMessageType.Error, toastMessage);
  }

  private setEmployeeTableData(employees: EmployeeSummary[]): void {
    this.loading.set(false);
    this.employeeData = employees.map((employee) => ({
      ...employee,
      name: `${employee.firstName ?? ''} ${employee.lastName ?? ''}`
    }));
    this.employeeFilteredData.set([...this.employeeData]);
    this.employeesList = [...new Set(this.employeeData.map((x) => x.name ?? ''))];
    this.employeesFilteredList.set([...this.employeesList]);
    this.dataSource = new MatTableDataSource<EmployeeSummary>(this.employeeData);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.cdr.markForCheck();
  }
}
